// localPass web client — all encryption/decryption happens here; the server
// only ever sees ciphertext. The DEK lives in memory only and is dropped on
// lock / reload / idle.

import {
  PBKDF2_ITERS,
  aesDecrypt,
  aesEncrypt,
  decodeJson,
  deriveKeys,
  encodeJson,
  fromB64,
  randomBytes,
  toB64,
  unwrapDek,
  wrapDek,
} from "../shared/crypto";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // keep in sync with wrangler.toml
const IDLE_LOCK_MS = 10 * 60 * 1000;
const CLIPBOARD_CLEAR_MS = 20 * 1000;

interface Entry {
  title: string;
  username: string;
  password: string;
  url: string;
  notes: string;
}

interface Item {
  id: string;
  entry: Entry;
  updated_at: number;
}

interface AttachmentMeta {
  id: string;
  filename: string;
  mime: string;
  size: number;
}

// ---- state -----------------------------------------------------------------

let dek: CryptoKey | null = null;
let items: Item[] = [];
let username = "";
let query = "";
let idleTimer: ReturnType<typeof setTimeout> | undefined;

// Kept for the change-password re-wrap: the wrapped DEK (ciphertext, safe to
// hold) plus the current KDF params. Cleared on lock along with the DEK.
let wrappedDek: { iv: string; ct: string } | null = null;
let kdfSalt = "";
let kdfIters = 0;

const app = document.getElementById("app")!;

// ---- tiny DOM helper (XSS-safe: user data goes through textContent) --------

type Attrs = Record<string, unknown>;
function h(tag: string, attrs: Attrs = {}, ...children: unknown[]): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") e.className = String(v);
    else if (k === "value") (e as HTMLInputElement).value = String(v);
    else if (k.startsWith("on") && typeof v === "function")
      e.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (v === true) e.setAttribute(k, "");
    else e.setAttribute(k, String(v));
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    e.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return e;
}

function mount(node: HTMLElement) {
  app.replaceChildren(node);
}

function toast(msg: string) {
  const wrap = document.getElementById("toast")!;
  wrap.firstElementChild!.textContent = msg;
  wrap.style.opacity = "1";
  setTimeout(() => (wrap.style.opacity = "0"), 2200);
}

// ---- api -------------------------------------------------------------------

async function api(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(path, { credentials: "same-origin", ...init });
}

async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await api(path, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `${res.status}`);
  }
  return (await res.json()) as T;
}

// ---- crypto helpers over the DEK -------------------------------------------

async function sealEntry(entry: Entry): Promise<{ iv: string; ciphertext: string }> {
  const s = await aesEncrypt(dek!, encodeJson(entry));
  return { iv: toB64(s.iv), ciphertext: toB64(s.ct) };
}

async function openEntry(iv: string, ciphertext: string): Promise<Entry> {
  const pt = await aesDecrypt(dek!, { iv: fromB64(iv), ct: fromB64(ciphertext) });
  return decodeJson<Entry>(pt);
}

// ---- idle auto-lock --------------------------------------------------------

function resetIdle() {
  if (!dek) return;
  clearTimeout(idleTimer);
  idleTimer = setTimeout(lock, IDLE_LOCK_MS);
}
for (const ev of ["click", "keydown", "mousemove", "touchstart"]) {
  window.addEventListener(ev, resetIdle, { passive: true });
}

function lock() {
  dek = null;
  items = [];
  query = "";
  wrappedDek = null;
  kdfSalt = "";
  kdfIters = 0;
  clearTimeout(idleTimer);
  renderUnlock();
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(`${label} copied — clears in 20s`);
    setTimeout(async () => {
      try {
        // Don't clobber the clipboard if the user has since copied something else.
        if ((await navigator.clipboard.readText()) !== text) return;
      } catch {
        // Can't read it back (permissions/unsupported) — clear anyway.
      }
      await navigator.clipboard.writeText("").catch(() => {});
    }, CLIPBOARD_CLEAR_MS);
  } catch {
    toast("Clipboard unavailable");
  }
}

// ---- unlock screen ---------------------------------------------------------

function renderUnlock() {
  const u = h("input", {
    type: "text",
    placeholder: "Username",
    autocomplete: "username",
    value: username,
    class: inputCls,
  }) as HTMLInputElement;
  const p = h("input", {
    type: "password",
    placeholder: "Master password",
    autocomplete: "current-password",
    class: inputCls,
  }) as HTMLInputElement;
  const err = h("p", { class: "text-sm text-rose-400 h-5" });
  const btn = h("button", { type: "submit", class: btnPrimary }, "Unlock");

  const form = h(
    "form",
    {
      class: "w-full max-w-sm space-y-4",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        err.textContent = "";
        btn.textContent = "Unlocking…";
        (btn as HTMLButtonElement).disabled = true;
        try {
          await unlock(u.value.trim(), p.value);
        } catch (ex) {
          err.textContent = (ex as Error).message || "Unlock failed";
          btn.textContent = "Unlock";
          (btn as HTMLButtonElement).disabled = false;
          p.value = "";
        }
      },
    },
    h("div", { class: "mb-2 text-center" },
      h("h1", { class: "text-2xl font-semibold tracking-tight" }, "localPass"),
      h("p", { class: "text-sm text-slate-400" }, "Your vault is decrypted only in this browser."),
    ),
    u,
    p,
    err,
    btn,
  );

  mount(
    h("div", { class: "flex min-h-screen items-center justify-center p-6" }, form),
  );
  u.focus();
  if (username) p.focus();
}

async function unlock(user: string, password: string) {
  if (!user || !password) throw new Error("Enter username and password");
  username = user;
  const params = await apiJson<{ kdf_salt: string; kdf_iters: number }>(
    `/api/auth/params?u=${encodeURIComponent(user)}`,
  );
  kdfSalt = params.kdf_salt;
  kdfIters = params.kdf_iters;
  const { kek, authKey } = await deriveKeys(password, fromB64(params.kdf_salt), params.kdf_iters);

  const login = await apiJson<{
    username: string;
    wrapped_dek_iv: string;
    wrapped_dek_ct: string;
  }>("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username: user, auth_key: toB64(authKey) }),
  });

  wrappedDek = { iv: login.wrapped_dek_iv, ct: login.wrapped_dek_ct };
  dek = await unwrapDek(kek, {
    iv: fromB64(login.wrapped_dek_iv),
    ct: fromB64(login.wrapped_dek_ct),
  });
  resetIdle();
  await loadVault();
}

// ---- vault list ------------------------------------------------------------

async function loadVault() {
  const data = await apiJson<{
    secrets: { id: string; iv: string; ciphertext: string; updated_at: number }[];
  }>("/api/secrets");
  items = [];
  for (const row of data.secrets) {
    try {
      items.push({
        id: row.id,
        entry: await openEntry(row.iv, row.ciphertext),
        updated_at: row.updated_at,
      });
    } catch {
      // skip undecryptable rows rather than crash the whole list
    }
  }
  renderList();
}

function topBar(...right: unknown[]) {
  return h(
    "header",
    { class: "flex items-center justify-between border-b border-white/10 px-4 py-3" },
    h("div", { class: "flex items-center gap-2" },
      h("span", { class: "text-lg font-semibold" }, "localPass"),
      h("span", { class: "text-xs text-slate-500" }, username),
    ),
    h("div", { class: "flex items-center gap-2" }, ...right),
  );
}

function renderList() {
  const search = h("input", {
    type: "search",
    placeholder: "Search…",
    value: query,
    class: inputCls + " mb-3",
    oninput: (e: Event) => {
      query = (e.target as HTMLInputElement).value;
      renderRows();
    },
  });

  const rows = h("ul", { class: "space-y-1" });
  function renderRows() {
    const list = items
      .filter((it) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          it.entry.title.toLowerCase().includes(q) ||
          it.entry.username.toLowerCase().includes(q) ||
          it.entry.url.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.entry.title.localeCompare(b.entry.title));
    rows.replaceChildren(
      ...(list.length
        ? list.map((it) =>
            h(
              "li",
              {},
              h(
                "button",
                {
                  class:
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-white/5",
                  onclick: () => renderDetail(it.id),
                },
                h("div", {},
                  h("div", { class: "font-medium" }, it.entry.title || "(untitled)"),
                  h("div", { class: "text-sm text-slate-400" }, it.entry.username || it.entry.url || ""),
                ),
                h("span", { class: "text-slate-600" }, "›"),
              ),
            ),
          )
        : [h("li", { class: "px-3 py-8 text-center text-sm text-slate-500" }, "No entries yet.")]),
    );
  }
  renderRows();

  mount(
    h("div", { class: "mx-auto max-w-2xl" },
      topBar(
        h("button", { class: btnGhost, onclick: () => renderEdit() }, "+ New"),
        h("button", { class: btnGhost, onclick: renderChangePassword }, "Password"),
        h("button", { class: btnGhost, onclick: lock }, "Lock"),
      ),
      h("main", { class: "p-4" }, search, rows),
    ),
  );
}

// ---- detail ----------------------------------------------------------------

async function renderDetail(id: string) {
  const it = items.find((x) => x.id === id);
  if (!it) return renderList();
  const e = it.entry;

  const field = (label: string, value: string, opts: { secret?: boolean; copy?: boolean } = {}) => {
    if (!value) return null;
    const valueEl = h("span", { class: "break-all font-mono text-sm" }, opts.secret ? "••••••••••" : value);
    let revealed = false;
    return h("div", { class: "rounded-lg bg-white/5 px-3 py-2" },
      h("div", { class: "flex items-center justify-between gap-2" },
        h("span", { class: "text-xs uppercase tracking-wide text-slate-400" }, label),
        h("div", { class: "flex gap-2" },
          opts.secret &&
            h("button", { class: linkCls, onclick: () => {
              revealed = !revealed;
              valueEl.textContent = revealed ? value : "••••••••••";
            } }, "show"),
          opts.copy &&
            h("button", { class: linkCls, onclick: () => copy(value, label) }, "copy"),
        ),
      ),
      valueEl,
    );
  };

  const attachmentsBox = h("div", { class: "space-y-2" }, h("p", { class: "text-sm text-slate-500" }, "Loading attachments…"));

  mount(
    h("div", { class: "mx-auto max-w-2xl" },
      topBar(
        h("button", { class: btnGhost, onclick: renderList }, "← Back"),
        h("button", { class: btnGhost, onclick: () => renderEdit(id) }, "Edit"),
        h("button", { class: btnGhost, onclick: lock }, "Lock"),
      ),
      h("main", { class: "space-y-3 p-4" },
        h("h2", { class: "text-xl font-semibold" }, e.title || "(untitled)"),
        field("Username", e.username, { copy: true }),
        field("Password", e.password, { secret: true, copy: true }),
        e.url && h("div", { class: "rounded-lg bg-white/5 px-3 py-2" },
          h("div", { class: "text-xs uppercase tracking-wide text-slate-400" }, "URL"),
          isHttpUrl(e.url)
            ? h("a", { href: e.url, target: "_blank", rel: "noopener noreferrer", class: "break-all text-sm text-sky-400 hover:underline" }, e.url)
            : h("p", { class: "break-all text-sm" }, e.url),
        ),
        e.notes && h("div", { class: "rounded-lg bg-white/5 px-3 py-2" },
          h("div", { class: "text-xs uppercase tracking-wide text-slate-400" }, "Notes"),
          h("p", { class: "whitespace-pre-wrap text-sm" }, e.notes),
        ),
        h("div", { class: "pt-2" },
          h("h3", { class: "mb-2 text-sm font-semibold text-slate-300" }, "Attachments"),
          attachmentsBox,
        ),
        h("div", { class: "pt-4" },
          h("button", { class: btnDanger, onclick: () => removeSecret(id) }, "Delete entry"),
        ),
      ),
    ),
  );
  void loadAttachments(id, attachmentsBox);
}

async function loadAttachments(secretId: string, box: HTMLElement) {
  let metas: AttachmentMeta[] = [];
  try {
    const data = await apiJson<{
      attachments: { id: string; name_iv: string; name_ct: string; file_iv: string; size: number }[];
    }>(`/api/secrets/${secretId}/attachments`);
    metas = await Promise.all(
      data.attachments.map(async (a) => {
        const pt = await aesDecrypt(dek!, { iv: fromB64(a.name_iv), ct: fromB64(a.name_ct) });
        const meta = decodeJson<{ filename: string; mime: string }>(pt);
        return { id: a.id, filename: meta.filename, mime: meta.mime, size: a.size };
      }),
    );
  } catch (ex) {
    box.replaceChildren(h("p", { class: "text-sm text-rose-400" }, (ex as Error).message));
    return;
  }

  const fileInput = h("input", { type: "file", class: "hidden" }) as HTMLInputElement;
  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (f) void uploadAttachment(secretId, f, box);
    fileInput.value = "";
  });

  box.replaceChildren(
    ...(metas.length
      ? metas.map((m) =>
          h("div", { class: "flex items-center justify-between rounded-lg bg-white/5 px-3 py-2" },
            h("div", { class: "min-w-0" },
              h("div", { class: "truncate text-sm" }, m.filename),
              h("div", { class: "text-xs text-slate-500" }, humanSize(m.size)),
            ),
            h("div", { class: "flex gap-2" },
              h("button", { class: linkCls, onclick: () => downloadAttachment(m) }, "download"),
              h("button", { class: linkCls + " text-rose-400", onclick: () => deleteAttachment(secretId, m.id, box) }, "delete"),
            ),
          ),
        )
      : [h("p", { class: "text-sm text-slate-500" }, "None.")]),
    fileInput,
    h("button", { class: btnGhost + " mt-1", onclick: () => fileInput.click() }, "+ Add file"),
  );
}

async function uploadAttachment(secretId: string, file: File, box: HTMLElement) {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    toast(`File too large (max ${humanSize(MAX_ATTACHMENT_BYTES)})`);
    return;
  }
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const fileSealed = await aesEncrypt(dek!, bytes);
    const nameSealed = await aesEncrypt(dek!, encodeJson({ filename: file.name, mime: file.type || "application/octet-stream" }));
    const res = await api(`/api/secrets/${secretId}/attachments`, {
      method: "POST",
      headers: {
        "content-type": "application/octet-stream",
        "x-name-iv": toB64(nameSealed.iv),
        "x-name-ct": toB64(nameSealed.ct),
        "x-file-iv": toB64(fileSealed.iv),
      },
      body: fileSealed.ct,
    });
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(b.error ?? `${res.status}`);
    }
    toast("File added");
    await loadAttachments(secretId, box);
  } catch (ex) {
    toast((ex as Error).message || "Upload failed");
  }
}

async function downloadAttachment(meta: AttachmentMeta) {
  try {
    const res = await api(`/api/attachments/${meta.id}`);
    if (!res.ok) throw new Error(`${res.status}`);
    const fileIv = res.headers.get("x-file-iv")!;
    const ct = new Uint8Array(await res.arrayBuffer());
    const plain = await aesDecrypt(dek!, { iv: fromB64(fileIv), ct });
    const blob = new Blob([plain], { type: meta.mime });
    const url = URL.createObjectURL(blob);
    const a = h("a", { href: url, download: meta.filename }) as HTMLAnchorElement;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (ex) {
    toast((ex as Error).message || "Download failed");
  }
}

async function deleteAttachment(secretId: string, id: string, box: HTMLElement) {
  if (!confirm("Delete this attachment?")) return;
  try {
    await apiJson(`/api/attachments/${id}`, { method: "DELETE" });
    await loadAttachments(secretId, box);
  } catch (ex) {
    toast((ex as Error).message || "Delete failed");
  }
}

// ---- create / edit ---------------------------------------------------------

function renderEdit(id?: string) {
  const existing = id ? items.find((x) => x.id === id)?.entry : undefined;
  const f = (name: keyof Entry, label: string, type = "text") => {
    const input = h(
      type === "textarea" ? "textarea" : "input",
      {
        ...(type === "textarea" ? { rows: 3 } : { type }),
        placeholder: label,
        value: existing?.[name] ?? "",
        class: inputCls,
        name,
      },
    ) as HTMLInputElement;
    return { input, row: h("label", { class: "block space-y-1" }, h("span", { class: "text-xs uppercase tracking-wide text-slate-400" }, label), input) };
  };

  const title = f("title", "Title");
  const user = f("username", "Username");
  const pass = f("password", "Password");
  const url = f("url", "URL");
  const notes = f("notes", "Notes", "textarea");
  const err = h("p", { class: "h-5 text-sm text-rose-400" });

  const form = h("form", {
    class: "space-y-3",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      const entry: Entry = {
        title: title.input.value.trim(),
        username: user.input.value,
        password: pass.input.value,
        url: url.input.value.trim(),
        notes: notes.input.value,
      };
      if (!entry.title && !entry.username && !entry.password) {
        err.textContent = "Add at least a title, username, or password.";
        return;
      }
      try {
        const sealed = await sealEntry(entry);
        if (id) {
          await apiJson(`/api/secrets/${id}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(sealed),
          });
        } else {
          await apiJson("/api/secrets", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(sealed),
          });
        }
        await loadVault();
      } catch (ex) {
        err.textContent = (ex as Error).message || "Save failed";
      }
    },
  },
    title.row, user.row,
    h("div", { class: "space-y-1" },
      pass.row,
      h("button", { type: "button", class: linkCls, onclick: () => (pass.input.value = genPassword()) }, "generate strong password"),
    ),
    url.row, notes.row, err,
    h("div", { class: "flex gap-2 pt-2" },
      h("button", { type: "submit", class: btnPrimary }, id ? "Save" : "Create"),
      h("button", { type: "button", class: btnGhost, onclick: () => (id ? renderDetail(id) : renderList()) }, "Cancel"),
    ),
  );

  mount(
    h("div", { class: "mx-auto max-w-2xl" },
      topBar(h("button", { class: btnGhost, onclick: () => (id ? renderDetail(id) : renderList()) }, "← Back")),
      h("main", { class: "p-4" },
        h("h2", { class: "mb-3 text-xl font-semibold" }, id ? "Edit entry" : "New entry"),
        form,
      ),
    ),
  );
  title.input.focus();
}

async function removeSecret(id: string) {
  if (!confirm("Delete this entry and its attachments? This cannot be undone.")) return;
  try {
    await apiJson(`/api/secrets/${id}`, { method: "DELETE" });
    await loadVault();
  } catch (ex) {
    toast((ex as Error).message || "Delete failed");
  }
}

// ---- change master password ------------------------------------------------

function renderChangePassword() {
  const cur = h("input", { type: "password", placeholder: "Current master password", autocomplete: "current-password", class: inputCls }) as HTMLInputElement;
  const nw = h("input", { type: "password", placeholder: "New master password", autocomplete: "new-password", class: inputCls }) as HTMLInputElement;
  const nw2 = h("input", { type: "password", placeholder: "Confirm new password", autocomplete: "new-password", class: inputCls }) as HTMLInputElement;
  const err = h("p", { class: "h-5 text-sm text-rose-400" });
  const btn = h("button", { type: "submit", class: btnPrimary }, "Change password") as HTMLButtonElement;

  const form = h("form", {
    class: "space-y-3",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      err.textContent = "";
      if (nw.value.length < 8) { err.textContent = "New password must be at least 8 characters."; return; }
      if (nw.value !== nw2.value) { err.textContent = "New passwords do not match."; return; }
      btn.disabled = true;
      btn.textContent = "Changing…";
      try {
        await changePassword(cur.value, nw.value);
        toast("Master password changed");
        renderList();
      } catch (ex) {
        err.textContent = (ex as Error).message || "Change failed";
        btn.disabled = false;
        btn.textContent = "Change password";
      }
    },
  },
    h("p", { class: "text-sm text-slate-400" }, "Re-wraps your vault key under a new password. Your entries are not re-encrypted and stay intact."),
    cur, nw, nw2, err, btn,
  );

  mount(
    h("div", { class: "mx-auto max-w-2xl" },
      topBar(h("button", { class: btnGhost, onclick: renderList }, "← Back")),
      h("main", { class: "p-4" },
        h("h2", { class: "mb-3 text-xl font-semibold" }, "Change master password"),
        form,
      ),
    ),
  );
  cur.focus();
}

async function changePassword(current: string, next: string) {
  if (!dek || !wrappedDek) throw new Error("Vault is locked");

  // 1. Re-derive the current key and unwrap the raw DEK. This both proves the
  //    current password locally and gives us the key bytes to re-wrap.
  const { kek: kekCur, authKey: authCur } = await deriveKeys(current, fromB64(kdfSalt), kdfIters);
  let rawDek: Uint8Array;
  try {
    rawDek = await aesDecrypt(kekCur, { iv: fromB64(wrappedDek.iv), ct: fromB64(wrappedDek.ct) });
  } catch {
    throw new Error("Current password is incorrect");
  }

  // 2. Derive a new key from the new password against a fresh salt, then wrap
  //    the SAME DEK with it — no secrets need re-encrypting.
  const newSalt = randomBytes(16);
  const { kek: kekNew, authKey: authNew } = await deriveKeys(next, newSalt, PBKDF2_ITERS);
  const newWrapped = await wrapDek(kekNew, rawDek);
  rawDek.fill(0); // best-effort scrub of the transient key bytes

  // 3. Persist. The server verifies the current auth key before applying.
  await apiJson("/api/auth/change-password", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      current_auth_key: toB64(authCur),
      new_auth_key: toB64(authNew),
      new_kdf_salt: toB64(newSalt),
      new_kdf_iters: PBKDF2_ITERS,
      new_wrapped_dek_iv: toB64(newWrapped.iv),
      new_wrapped_dek_ct: toB64(newWrapped.ct),
    }),
  });

  // 4. Update local session state so the next change uses the new wrap. The DEK
  //    itself is unchanged, so `dek` stays valid.
  kdfSalt = toB64(newSalt);
  kdfIters = PBKDF2_ITERS;
  wrappedDek = { iv: toB64(newWrapped.iv), ct: toB64(newWrapped.ct) };
}

// ---- utils -----------------------------------------------------------------

function humanSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function isHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s);
}

function genPassword(len = 20): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+";
  const n = alphabet.length;
  const limit = Math.floor(256 / n) * n; // reject bytes >= limit to kill modulo bias
  const buf = new Uint8Array(64);
  let out = "";
  while (out.length < len) {
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b >= limit) continue;
      out += alphabet[b % n];
      if (out.length === len) break;
    }
  }
  return out;
}

// ---- styles (tailwind utility strings) -------------------------------------

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-slate-500 focus:border-sky-500";
const btnPrimary =
  "w-full rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium hover:bg-sky-500 disabled:opacity-60";
const btnGhost =
  "rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5";
const btnDanger =
  "rounded-lg bg-rose-600/90 px-4 py-2 text-sm font-medium hover:bg-rose-600";
const linkCls = "text-xs text-sky-400 hover:underline";

// ---- boot ------------------------------------------------------------------

renderUnlock();
